import { BenefitTypeOptions } from '@strata/constants';
import { DateInputFormik } from '@strata/react-date-time';
import { WarningDialog } from '@strata/react-dialog';
import { FormContainer } from '@strata/react-form-container';
import { TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { dialogStrings, validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { ProductPayerConfigBulkUploadEmbed } from 'pages/productConfig/ProductPayerConfigBulkUploadEmbed';
import { uploadProductPayerConfigFile } from 'pages/productConfig/ProductPayerConfigBulkUploadSupport';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { put, useGet } from 'services/Http';

const TOAST_TIMEOUT_MS = 5000;

const GO_LIVE_TYPE = {
    CAG: 'cag',
    PRODUCT_EXPANSION: 'product_expansion',
};

const getProductExpansionId = (createResponse) =>
    typeof createResponse === 'string' ? createResponse : null;

export const GoLiveDetails = () => {
    const { segmentId, goLiveId } = useParams();

    const navigate = useNavigate();

    const goHome = () => navigate(`/go-lives/${segmentId}`);
    const isCreate = goLiveId === CREATE_ROUTE;
    const segment = useGet(`/reference/payer_segments/${segmentId}`);

    const goLive = useGet(!isCreate ? `/reference/go_lives/${goLiveId}` : null);

    const [showWarning, setShowWarning] = useState(false);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);

    const navigateToProductExpansionEdit = (productExpansionId, validationErrors) => {
        SuccessToast({
            message: 'Product Expansion Created Successfully!',
            timeout: TOAST_TIMEOUT_MS,
        });
        navigate(`/go-lives/${segmentId}/product-expansion/${productExpansionId}`, {
            state: validationErrors ? { validationErrors } : undefined,
        });
    };

    const upsert = async (values) => {
        try {
            if (isCreate && values.goLiveType === GO_LIVE_TYPE.PRODUCT_EXPANSION) {
                const createResponse = await put(
                    `/reference/product_expansions/${segmentId}/${CREATE_ROUTE}`,
                    {
                        product_expansion_name: values.productExpansionName,
                        product_expansion_date: values.productExpansionDate,
                        outreach_date: values.outreachDate,
                        go_live_date: values.goLiveDate,
                        benefit: values.benefit,
                    },
                );
                const productExpansionId = getProductExpansionId(createResponse);

                if (values.fileToUpload) {
                    try {
                        const validationErrors = await uploadProductPayerConfigFile(
                            [segmentId],
                            values.fileToUpload,
                            productExpansionId
                                ? { product_expansion_id: productExpansionId }
                                : undefined,
                        );
                        if (validationErrors) {
                            handleError('File not uploaded. Invalid values found in file.');
                            navigateToProductExpansionEdit(productExpansionId, validationErrors);
                            return;
                        }
                    } catch (uploadErr) {
                        handleError('Product expansion created but file upload failed.', uploadErr);
                        if (productExpansionId) {
                            navigateToProductExpansionEdit(productExpansionId);
                        }
                        return;
                    }
                }

                if (productExpansionId) {
                    navigateToProductExpansionEdit(productExpansionId);
                    return;
                }

                SuccessToast({
                    message: 'Product Expansion Created Successfully!',
                    timeout: TOAST_TIMEOUT_MS,
                });
                goHome();
                return;
            }

            await put(`/reference/go_lives/${segmentId}/${goLiveId}`, {
                go_live_name: values.goLiveName,
                platform_start_date: values.platformStartDate,
                outreach_date: values.outreachDate,
                go_live_date: values.goLiveDate,
            });
            SuccessToast({
                message: `Go-Live ${isCreate ? 'Created' : 'Updated'} Successfully!`,
                timeout: TOAST_TIMEOUT_MS,
            });
            goHome();
        } catch (err) {
            handleError(
                values.goLiveType === GO_LIVE_TYPE.PRODUCT_EXPANSION
                    ? 'Error saving product expansion.'
                    : 'Error saving go-live.',
                err,
            );
        }
    };

    return (
        <Formik
            initialValues={{
                goLiveType: GO_LIVE_TYPE.CAG,
                segmentName: segment?.segment_name,
                goLiveName: goLive?.go_live_name || undefined,
                platformStartDate: goLive?.platform_start_date || undefined,
                productExpansionName: undefined,
                productExpansionDate: undefined,
                benefit: undefined,
                outreachDate: goLive?.outreach_date || undefined,
                goLiveDate: goLive?.go_live_date || undefined,
                productSegmentConfigFile: undefined,
                fileToUpload: undefined,
                validationErrors: undefined,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                goLiveType: Yup.string().required(validationStrings.requiredField.errorMessage),
                goLiveName: Yup.string().when('goLiveType', {
                    is: GO_LIVE_TYPE.PRODUCT_EXPANSION,
                    otherwise: (schema) =>
                        schema.required(validationStrings.requiredField.errorMessage),
                }),
                platformStartDate: DateInputFormik.validator().when('goLiveType', {
                    is: GO_LIVE_TYPE.PRODUCT_EXPANSION,
                    otherwise: (schema) =>
                        schema.required(validationStrings.requiredField.errorMessage),
                }),
                productExpansionName: Yup.string().when('goLiveType', {
                    is: GO_LIVE_TYPE.PRODUCT_EXPANSION,
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                productExpansionDate: DateInputFormik.validator().when('goLiveType', {
                    is: GO_LIVE_TYPE.PRODUCT_EXPANSION,
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                benefit: Yup.string().when('goLiveType', {
                    is: GO_LIVE_TYPE.PRODUCT_EXPANSION,
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                outreachDate: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                goLiveDate: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
            })}
            onSubmit={upsert}
        >
            {(formik) => {
                const isProductExpansion =
                    isCreate && formik.values.goLiveType === GO_LIVE_TYPE.PRODUCT_EXPANSION;
                let formTitle;
                if (isCreate) {
                    formTitle = isProductExpansion ? 'Create Product Expansion' : 'Create Go-Live';
                } else {
                    formTitle = 'Edit Go-Live';
                }

                return (
                    <>
                        <WarningDialog
                            dialogTitle={dialogStrings.unsavedChanges.title}
                            isOpen={showWarning}
                            onSecondaryButtonClick={() => setShowWarning(false)}
                            onPrimaryButtonClick={goHome}
                            dialogContent={<p>{dialogStrings.unsavedChanges.body}</p>}
                        />
                        <FormContainer
                            formTitle={formTitle}
                            isLoading={!goLive && !isCreate}
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                            onSecondaryButtonClick={() => {
                                if (formik.dirty) {
                                    setShowWarning(true);
                                } else {
                                    goHome();
                                }
                            }}
                            formLeftContent={
                                <>
                                    <TextInputFormik
                                        label="Payer Segment"
                                        id="segmentName"
                                        required
                                        disabled
                                    />
                                    {isCreate && (
                                        <RadioButtonGroupFormik
                                            name="goLiveType"
                                            label="Go-Live Type"
                                            required
                                            options={[
                                                { value: GO_LIVE_TYPE.CAG, text: 'CAG Go-live' },
                                                {
                                                    value: GO_LIVE_TYPE.PRODUCT_EXPANSION,
                                                    text: 'Product Expansion',
                                                },
                                            ]}
                                        />
                                    )}
                                    {isProductExpansion ? (
                                        <>
                                            <TextInputFormik
                                                label="Product Expansion Name"
                                                id="productExpansionName"
                                                required
                                            />
                                            <SuggestSingleSelectFormik
                                                id="benefit"
                                                label="Benefit"
                                                required
                                                values={BenefitTypeOptions}
                                                selectedItem={BenefitTypeOptions.find(
                                                    ({ value }) => value === formik.values.benefit,
                                                )}
                                                onChange={(newValue) =>
                                                    formik.setFieldValue('benefit', newValue?.value)
                                                }
                                            />
                                            <DateInputFormik
                                                label="Platform Start Date"
                                                id="productExpansionDate"
                                                required
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <TextInputFormik
                                                label="Go-Live Name"
                                                id="goLiveName"
                                                required
                                            />
                                            <DateInputFormik
                                                label="Platform Start Date"
                                                id="platformStartDate"
                                                required
                                            />
                                        </>
                                    )}
                                    <DateInputFormik
                                        label="Outreach Date"
                                        id="outreachDate"
                                        required
                                    />
                                    <DateInputFormik
                                        label="Go-Live Date"
                                        id="goLiveDate"
                                        required
                                    />
                                    {isProductExpansion && (
                                        <ProductPayerConfigBulkUploadEmbed
                                            showValidationErrors
                                            validationErrors={formik.values.validationErrors}
                                            showValidationErrorDialog={showValidationErrorDialog}
                                            onCloseErrorDialog={() =>
                                                setShowValidationErrorDialog(false)
                                            }
                                            onOpenErrorDialog={() =>
                                                setShowValidationErrorDialog(true)
                                            }
                                            onFileChange={() =>
                                                formik.setFieldValue('validationErrors', undefined)
                                            }
                                        />
                                    )}
                                </>
                            }
                        />
                    </>
                );
            }}
        </Formik>
    );
};
